import { useState, useEffect } from "react";
const BASE = "https://imalishawelfare.com/api";
const tok = () => sessionStorage.getItem("token");

export default function Analytics() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE}/applications`, { headers:{ Authorization:"Bearer "+tok() } })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setApps(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>Loading analytics...</div>;

  const total = apps.length;
  const approved = apps.filter(a => a.status === "APPROVED" || a.status === "DISBURSED").length;
  const disbursed = apps.filter(a => a.status === "DISBURSED").length;
  const pending = apps.filter(a => a.status === "PENDING").length;
  const rejected = apps.filter(a => a.status === "REJECTED").length;
  const approvalRate = total > 0 ? ((approved / total) * 100).toFixed(1) : 0;
  const totalFees = apps.reduce((s,a) => s + (a.loanFeeAmount||0), 0);
  const totalLoans = apps.reduce((s,a) => s + (a.selectedLoanAmount||0), 0);
  const feePaid = apps.filter(a => a.loanStatus === "FEE_PAID").length;

  // By county
  const byCounty = {};
  apps.forEach(a => {
    if (a.county) byCounty[a.county] = (byCounty[a.county]||0) + 1;
  });
  const countyList = Object.entries(byCounty).sort((a,b)=>b[1]-a[1]);
  const maxCounty = countyList[0]?.[1] || 1;

  // By category
  const byCat = {};
  apps.forEach(a => {
    const cat = (a.category||"UNKNOWN").replace(/_/g," ");
    byCat[cat] = (byCat[cat]||0) + 1;
  });
  const catList = Object.entries(byCat).sort((a,b)=>b[1]-a[1]);
  const maxCat = catList[0]?.[1] || 1;

  // By status
  const byStatus = { PENDING:pending, APPROVED:approved, DISBURSED:disbursed, REJECTED:rejected };

  // By purpose
  const byPurpose = {};
  apps.forEach(a => {
    const j = {};
    (a.justification||"").split("|").forEach(p => { const i=p.indexOf(":"); if(i>0) j[p.slice(0,i).trim()]=p.slice(i+1).trim(); });
    if (j["Purpose"]) byPurpose[j["Purpose"]] = (byPurpose[j["Purpose"]]||0) + 1;
  });
  const purposeList = Object.entries(byPurpose).sort((a,b)=>b[1]-a[1]).slice(0,6);
  const maxPurpose = purposeList[0]?.[1] || 1;

  // By month
  const byMonth = {};
  apps.forEach(a => {
    if (a.createdAt) {
      const m = a.createdAt.substring(0,7);
      if (!byMonth[m]) byMonth[m] = { total:0, approved:0, disbursed:0 };
      byMonth[m].total++;
      if (a.status==="APPROVED"||a.status==="DISBURSED") byMonth[m].approved++;
      if (a.status==="DISBURSED") byMonth[m].disbursed++;
    }
  });
  const monthList = Object.entries(byMonth).sort((a,b)=>a[0].localeCompare(b[0]));

  // By payment method
  const byPay = {};
  apps.forEach(a => {
    const j = {};
    (a.justification||"").split("|").forEach(p => { const i=p.indexOf(":"); if(i>0) j[p.slice(0,i).trim()]=p.slice(i+1).trim(); });
    const pay = j["Pay"]||"Unknown";
    byPay[pay] = (byPay[pay]||0) + 1;
  });

  const StatCard = ({ label, value, sub, color }) => (
    <div style={{ background:"white", borderRadius:10, padding:"20px 22px", boxShadow:"0 1px 4px rgba(0,0,0,0.06)", borderTop:`3px solid ${color}` }}>
      <p style={{ fontSize:11, color:"#94a3b8", fontWeight:600, margin:"0 0 8px", textTransform:"uppercase", letterSpacing:.5 }}>{label}</p>
      <p style={{ fontSize:26, fontWeight:800, color, margin:"0 0 4px" }}>{value}</p>
      {sub && <p style={{ fontSize:12, color:"#94a3b8", margin:0 }}>{sub}</p>}
    </div>
  );

  const Bar = ({ label, value, max, color }) => (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <span style={{ fontSize:13, color:"#334155" }}>{label}</span>
        <span style={{ fontSize:13, fontWeight:700, color }}>{value}</span>
      </div>
      <div style={{ background:"#f1f5f9", borderRadius:4, height:8 }}>
        <div style={{ width:`${(value/max)*100}%`, background:color, height:8, borderRadius:4, transition:"width 0.5s" }}/>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>

      {/* Stat cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:14, marginBottom:24 }}>
        <StatCard label="Total Applications" value={total} sub="All time" color="#0066cc"/>
        <StatCard label="Approval Rate" value={`${approvalRate}%`} sub={`${approved} approved`} color="#10b981"/>
        <StatCard label="Disbursed" value={disbursed} sub={`KSh ${totalLoans.toLocaleString()}`} color="#7c3aed"/>
        <StatCard label="Fees Collected" value={`KSh ${totalFees.toLocaleString()}`} sub={`${disbursed} payments`} color="#f59e0b"/>
        <StatCard label="Pending Review" value={pending} sub="Awaiting action" color="#ef4444"/>
        <StatCard label="Fee Paid" value={feePaid} sub="Awaiting disbursal" color="#0052a3"/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>

        {/* By County */}
        <div style={{ background:"white", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:"0 0 16px" }}>Applications by County</p>
          {countyList.length === 0 ? <p style={{ color:"#94a3b8", fontSize:13 }}>No data</p> :
            countyList.slice(0,8).map(([county,count],i) => (
              <Bar key={county} label={county} value={count} max={maxCounty} color={["#0066cc","#10b981","#f59e0b","#ef4444","#7c3aed","#0052a3","#059669","#d97706"][i%8]}/>
            ))
          }
        </div>

        {/* By Category */}
        <div style={{ background:"white", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:"0 0 16px" }}>Applications by Category</p>
          {catList.length === 0 ? <p style={{ color:"#94a3b8", fontSize:13 }}>No data</p> :
            catList.map(([cat,count],i) => (
              <Bar key={cat} label={cat} value={count} max={maxCat} color={["#0066cc","#10b981","#f59e0b","#ef4444"][i%4]}/>
            ))
          }
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>

        {/* By Loan Purpose */}
        <div style={{ background:"white", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:"0 0 16px" }}>Applications by Loan Purpose</p>
          {purposeList.length === 0 ? <p style={{ color:"#94a3b8", fontSize:13 }}>No data</p> :
            purposeList.map(([purpose,count],i) => (
              <Bar key={purpose} label={purpose} value={count} max={maxPurpose} color={["#7c3aed","#0066cc","#10b981","#f59e0b","#ef4444","#0052a3"][i%6]}/>
            ))
          }
        </div>

        {/* Status breakdown */}
        <div style={{ background:"white", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:"0 0 16px" }}>Application Status Breakdown</p>
          {Object.entries(byStatus).map(([status,count]) => {
            const colors = { PENDING:"#f59e0b", APPROVED:"#0066cc", DISBURSED:"#10b981", REJECTED:"#ef4444" };
            const pct = total > 0 ? ((count/total)*100).toFixed(1) : 0;
            return (
              <div key={status} style={{ marginBottom:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:13, color:"#334155", fontWeight:500 }}>{status}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:colors[status] }}>{count} ({pct}%)</span>
                </div>
                <div style={{ background:"#f1f5f9", borderRadius:4, height:10 }}>
                  <div style={{ width:`${pct}%`, background:colors[status], height:10, borderRadius:4 }}/>
                </div>
              </div>
            );
          })}
          <div style={{ marginTop:16, padding:"12px 16px", background:"#f8fafc", borderRadius:8 }}>
            <p style={{ fontSize:12, color:"#64748b", margin:0, fontWeight:600 }}>
              Conversion Rate: <span style={{ color:"#10b981" }}>{total>0?((disbursed/total)*100).toFixed(1):0}%</span> (applied → disbursed)
            </p>
          </div>
        </div>
      </div>

      {/* Monthly trends */}
      <div style={{ background:"white", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)", marginBottom:16 }}>
        <p style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:"0 0 16px" }}>Monthly Application Trends</p>
        {monthList.length === 0 ? (
          <p style={{ color:"#94a3b8", fontSize:13 }}>No data yet</p>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#f8fafc" }}>
                  {["Month","Total Applications","Approved","Disbursed","Approval Rate"].map(h=>(
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:600, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthList.map(([month,data]) => (
                  <tr key={month} style={{ borderTop:"1px solid #f1f5f9" }}>
                    <td style={{ padding:"10px 14px", fontWeight:600, color:"#1e293b" }}>{month}</td>
                    <td style={{ padding:"10px 14px", color:"#334155" }}>{data.total}</td>
                    <td style={{ padding:"10px 14px", color:"#0066cc", fontWeight:600 }}>{data.approved}</td>
                    <td style={{ padding:"10px 14px", color:"#10b981", fontWeight:600 }}>{data.disbursed}</td>
                    <td style={{ padding:"10px 14px", color:"#7c3aed", fontWeight:600 }}>
                      {data.total>0?((data.approved/data.total)*100).toFixed(1):0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment method + loan amounts */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <div style={{ background:"white", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:"0 0 16px" }}>Payment Methods</p>
          {Object.entries(byPay).map(([pay,count],i) => (
            <Bar key={pay} label={pay} value={count} max={total||1} color={["#0066cc","#10b981","#f59e0b"][i%3]}/>
          ))}
        </div>

        <div style={{ background:"white", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:"0 0 16px" }}>Financial Summary</p>
          {[
            ["Total Applications", total, "#0066cc"],
            ["Total Fees Collected", `KSh ${totalFees.toLocaleString()}`, "#10b981"],
            ["Total Loans Disbursed", `KSh ${totalLoans.toLocaleString()}`, "#7c3aed"],
            ["Avg Loan Amount", disbursed>0?`KSh ${Math.round(totalLoans/disbursed).toLocaleString()}`:"—", "#f59e0b"],
            ["Avg Fee Amount", disbursed>0?`KSh ${Math.round(totalFees/disbursed).toLocaleString()}`:"—", "#0052a3"],
          ].map(([label,value,color])=>(
            <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"9px 0", borderBottom:"1px solid #f1f5f9", fontSize:13 }}>
              <span style={{ color:"#64748b" }}>{label}</span>
              <span style={{ fontWeight:700, color }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

